import {
  IPlatformHeaderContent,
  IPlatformPage,
  OrderSavingsComparisonMap,
  PlatformPageSectionType,
  PlatformPageType,
} from '../types/platform-pages';

import { ANALYTICS_CALENDLY } from './calendly';
import { FAQType } from '../types/subscription';
import { NextRouter } from 'next/router';
import { STATIC_FILE_URL } from '@/shared/constants';
import blue1_email from 'public/images/platform/blue1-email.svg?url';
import blue1_loyalty_offer from 'public/images/platform/blue1-loyalty-offer.svg?url';
import blue1_shopping_bags from 'public/images/platform/blue1-shopping-bags.svg?url';

export const PLATFORM_PAGE_CLASS = {
  [PlatformPageType.IDEATION_AND_BRAND_SETUP]: 'ideation-and-brand-setup',
  [PlatformPageType.SOURCING_AND_PRODUCTION]: 'sourcing-and-production',
  [PlatformPageType.LOGISTICS_AND_FULFILLMENT]: 'logistics-and-fulfillment',
  [PlatformPageType.SALES_AND_MARKETING]: 'sales-and-marketing',
  [PlatformPageType.RETURNS]: 'returns',
  [PlatformPageType.ANALYTICS]: 'analytics',
  [PlatformPageType.DESIGN_STUDIO]: 'design-studio',
  [PlatformPageType.RETAIL_CONNECT]: 'retail-connect',
  [PlatformPageType.SOCIAL_ANALYSIS]: 'social-analysis',
  [PlatformPageType.CONTENT_STUDIO]: 'content-studio',
};

// Ideation & Brand Setup
export const BUSINESS_IDEATION_ICON = `${STATIC_FILE_URL}/7cc73097e3e83deb74c6aace3dd877a7.svg`;
export const PRODUCT_IDEATION_ICON = `${STATIC_FILE_URL}/134e6730677f391cc9c6fa52acdae7e8.svg`;
export const CREATE_YOUR_IDEA_ICON = `${STATIC_FILE_URL}/3d71d3f1217086b33c70d82bec3bd13e.svg`;
export const MARKETING_ASSET_CREATION_ICON = `${STATIC_FILE_URL}/b1eff8a4dbc3d984556246690d4eb07c.svg`;

// Sourcing & Production
export const TRUSTED_NETWORK_ICON = '/images/sourcing/landing-page/network.svg';
export const CUSTOM_PROJECTS_ICON = '/images/sourcing/landing-page/rfp.svg';
export const FIND_SUPPLIER_ICON = '/images/sourcing/landing-page/search.svg';
export const PROJECT_MANAGEMENT_ICON = '/images/sourcing/landing-page/chat.svg';

// Logistics & Fulfillment
export const SHIPPING_ICON = `${STATIC_FILE_URL}/35ae87a8c1a2be68f468f12a46fad8f8.svg`;
export const FULFILLMENT_ICON = `${STATIC_FILE_URL}/146f9f67315b739e751e87d6fb044bf7.svg`;
export const KITTING_ICON = `${STATIC_FILE_URL}/331790499541fb81111fa26164a56b95.svg`;
export const WHOLESALE_ICON = `${STATIC_FILE_URL}/aaa47fe6742cd29f3af1dca7d9d73079.svg`;

// Sales & Marketing
export const SALES_CHANELS_ICON = `${STATIC_FILE_URL}/77e28f8d28617d38f067b81ce68ec0a5.svg`;
export const PIETRA_APPS_ICON = `${STATIC_FILE_URL}/883a6b43789d771d15afeecd7c53ab54.svg`;
export const AFFILIATE_MARKETING_ICON = `${STATIC_FILE_URL}/927dda52aa624a75aa4f1913a90a7995.svg`;
export const STOREFRONT_ICON = `${STATIC_FILE_URL}/c57e67a0bec06f386658495fa871f090.svg`;

// Analytics
export const ECOMMERCE_ICON = `${STATIC_FILE_URL}/8d216100c9190bc3bbf1e17dade6c634.svg`;
export const ODOMETER_ICON = `${STATIC_FILE_URL}/609a78d9c2c0ab1540f81cac49dca9d3.svg`;
export const LIGHTBULB_ICON = `${STATIC_FILE_URL}/619dd5b055a62ff6c36771e7781a9df0.svg`;
export const GROW_SALES_ICON = `${STATIC_FILE_URL}/87b0bb10350fcb47f67600a16e323160.svg`;

export const IDEATION_AND_BRAND_SETUP_CONTENT: IPlatformPage = {
  type: PlatformPageType.IDEATION_AND_BRAND_SETUP,
  title: 'Ideation & Brand Setup',
  hero: {
    colors: {
      text: '#fff',
      background: 'linear-gradient(to bottom, #57ad6a 65%,  rgba(255, 64, 26, 0.2) 100%)',
    },
    class: 'ideation',
    bgImage: `${STATIC_FILE_URL}/891b487823c6febf99250c62f69ee100.png`,
    btnText: 'Create Free Account',
    description: 'Market research and design tools to help you expand product lines and bring products to market fast.',
    isHeroOverlayShown: true,
    showSignUp: true,
  },
  sections: [
    {
      type: PlatformPageSectionType.TEXT_GRID,
      preTitle: 'Ideation & Brand Setup',
      preTitleColor: '#ff5c3c',
      title: 'Dream up your next idea in minutes',
      titleColor: '#000000',
      description: 'Rapidly create new ideas and with one click get connected to a manufacturer to make it a reality.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#ffffff',
      buttons: [{ btnText: 'Sign Up for Free' }],
      textGridItems: [
        {
          icon: BUSINESS_IDEATION_ICON,
          title: 'Business ideation',
          description:
            "Create a business kit that showcases your brand's vision, market insights, and strategic plans.",
        },
        {
          icon: PRODUCT_IDEATION_ICON,
          title: 'Product ideation',
          description: "Dream up a product idea and get it made using Pietra's manufacturing network.",
        },
        {
          icon: CREATE_YOUR_IDEA_ICON,
          title: 'Create your idea',
          description: 'Connect with a supplier from our 1300+ vetted network to make your idea a reality.',
        },
        {
          icon: MARKETING_ASSET_CREATION_ICON,
          title: 'Marketing asset creation',
          description: 'Quickly create marketing collateral unique to your business for social and more.',
        },
      ],
    },
    {
      type: PlatformPageSectionType.CALL_TO_ACTION,
      preTitle: 'Designed for entrepreneurs',
      preTitleColor: '#FFFFFF',
      title: 'Create your next business plan in minutes',
      titleColor: '#FFFFFF',
      description: 'Tools for ideating and creating your next product included with your membership.',
      descriptionColor: 'rgba(255, 255, 255, 0.8)',
      bgColor: '#ff5c3c',
      buttons: [{ btnText: 'Sign Up for Free' }],
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Brand ideation',
      preTitleColor: '#ff5c3c',
      title: 'Identify an idea, market, and more',
      titleColor: '#000000',
      description:
        'Create and validate your next product idea with market research, product strategy, and marketing plan.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#FFFFFF',
      buttons: [{ btnText: 'Try Brand Ideation Now' }],
      image: `${STATIC_FILE_URL}/1ac06b2320f77b2f43e72a4849948e46.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Product Ideation',
      preTitleColor: '#ff5c3c',
      title: 'Dream up your next company',
      titleColor: '#000000',
      description:
        'Create a visual (or many) of that product you have been dreaming of making. Then get connected to a manufacturing partner that can make your dream a reality.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#F9F2F1',
      buttons: [{ btnText: 'Dream Up & Make Your Next Product Line' }],
      image: `${STATIC_FILE_URL}/1125ddfb731db1e133193aba0ae0c772.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Marketing ideation',
      preTitleColor: '#ff5c3c',
      titleColor: '#000000',
      title: 'Create marketing material unique to your business',
      description: 'Quickly create 100s of powerful marketing assets to drive sales and grow your business.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#FFFFFF',
      image: `${STATIC_FILE_URL}/df0299c793dbe00cb0bbe5e6927da42b.png`,
      isComingSoon: true,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Step-by-step guide',
      preTitleColor: '#ff5c3c',
      title: 'Use your business plan to create a product',
      titleColor: '#000000',
      description:
        'First time launching a product line? Start with our step-by-step guide and follow simple steps to start selling your product.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#F9F2F1',
      buttons: [{ btnText: 'View the Step-by-Step Guide' }],
      image: `${STATIC_FILE_URL}/f7eaa8ccbd49332d83a689cd6c96833c.png`,
    },
  ],
};

export const DESIGN_STUDIO_CONTENT: IPlatformPage = {
  type: PlatformPageType.DESIGN_STUDIO,
  title: 'Design a best-selling product using AI',
  hero: {
    colors: {
      text: '#fff',
      background: '#000',
    },
    isHeroOverlayShown: false,
    class: 'design-studio',
    btnText: 'Sign up for free',
    description: 'Use AI to design products & packaging and get it made with 1300+ factories.',
    showVideo: true,
    showSignUp: false,
    heroVideo: '894382523',
  },
  brandLogos: false,
  textWithHighlight: false,
  sections: [
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'ideation',
      preTitleColor: '#fff',
      titleColor: '#fff',
      title: 'Create designs and variations in seconds. ',
      description:
        'Start with reference images, sketches, or prompting to create design ideas for your next product line.',
      descriptionColor: '#fff',
      bgColor: '#000',
      buttons: [{ btnText: 'Get started' }],
      image: `${STATIC_FILE_URL}/c22f0a2bd6d22e0a2cf736617e6d1b0b.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'SKETCH To Designs',
      preTitleColor: '#fff',
      titleColor: '#fff',
      title: 'Turn sketches into 3D designs.',
      description: 'Give your design team a fast path to creating many different designs from basic concept sketches.',
      descriptionColor: '#fff',
      bgColor: '#000',
      buttons: [{ btnText: 'Get started' }],
      image: `${STATIC_FILE_URL}/5546788aa293cf2992b0fa5171161e3c.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Work smarter',
      preTitleColor: '#fff',
      titleColor: '#fff',
      title: 'Collaborate with your team and your own suppliers.',
      description: 'Invite your vendors and send them measurements and annotated designs for quicker sampling.',
      descriptionColor: '#fff',
      bgColor: '#000',
      buttons: [{ btnText: 'Get started' }],
      image: `${STATIC_FILE_URL}/332f124b4416b4317bea3ac5f9e20f4b.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Work faster',
      preTitleColor: '#fff',
      titleColor: '#fff',
      title: 'Thousands of templates to get you started.',
      description: 'Use templates from manufacturers to get ideas and faster and with confidence they can be made.',
      descriptionColor: '#fff',
      bgColor: '#000',
      buttons: [{ btnText: 'Get started' }],
      image: `${STATIC_FILE_URL}/003c66eb08ba09557ba78824ac62c707.png`,
    },
  ],
};

export const PLATFORM_V2_HERO_CONTENT = [
  {
    image: `${STATIC_FILE_URL}/f725c5f5c64ba77bebc9ab11ca419dd1.png`,
    title: 'Find global suppliers & avoid tariffs',
    subtitle: 'Secure better prices at lower MOQs with a vetted global factory network.',
  },
  {
    image: `${STATIC_FILE_URL}/926187975d0a1ea8c3625f74a7cf4b02.png`,
    title: 'Simplify vendor management',
    subtitle: 'Centralize design, collaboration, and POs for team coordination and vendor oversight.',
  },
  {
    image: `${STATIC_FILE_URL}/dde8d0bb717be81297c2a39fb5549cf3.png`,
    title: 'Work directly with partners',
    subtitle: 'Get factory-direct pricing with top suppliers and eliminate expensive agent fees.',
  },
  {
    image: `${STATIC_FILE_URL}/98a8261f53b65618462d22eb5ee83696.png`,
    title: 'On-demand quality control',
    subtitle: 'Ensure quality with factory and manufacturing inspections when you need them.',
  },
];

export const SOURCING_AND_PRODUCTION_UPLOADER_CONTENT = {
  products: {
    image: `${STATIC_FILE_URL}/8069f95f436bfae63831cf4f65dddffe.png`,
    title: 'Source products directly from top factories and save big',
    subtitle: 'Design and create custom merchandise with trusted U.S. and international suppliers vetted by Pietra.',
    desc: "See if Pietra's network can make or source your products",
    buttonText: 'Upload a product design file or brand deck',
    accept: 'PNG | JPG | WEBP | AI | PSD | PDF | PPT',
  },
  freelancers: {
    image: `${STATIC_FILE_URL}/f9f8c50bf8b741360e5a71f316746ae4.png`,
    title: 'Hire agencies & freelancers to support your brand',
    subtitle: 'Work with renowned and budget-friendly freelancers to create logos, tech packs, and brand materials.',
    desc: "See if Pietra's network can help with your needs",
    buttonText: 'Upload a design file or product idea',
  },
  packaging: {
    image: `${STATIC_FILE_URL}/db729b8429b9d21fe33fbb8cf7a96882.png`,
    title: 'Save up to 90% on branded e-commerce packaging',
    subtitle: 'Create branded mailer boxes, poly mailers, and more, directly from Pietra suppliers at the lowest cost.',
    desc: "See if Pietra's network can make or source your packaging",
    buttonText: 'Upload a design file or packaging idea',
  },
};

export const SOURCING_AND_PRODUCTION_CONTENT: IPlatformPage = {
  type: PlatformPageType.SOURCING_AND_PRODUCTION,
  title: 'Source custom products for your e-commerce brand.',
  sections: [
    {
      type: PlatformPageSectionType.TEXT_GRID,
      preTitle: 'Sourcing & Production',
      preTitleColor: '#ff5c3c',
      title: 'Source products, packaging, and services from the same place as top brands',
      titleColor: '#000000',
      description:
        'Pietra has a large network of trusted suppliers that work with the biggest brands in the world. Plus software to centralize comms, POs, and assets in one place.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#ffffff',
      buttons: [{ btnText: 'Sign up for free' }],
      textGridItems: [
        {
          icon: TRUSTED_NETWORK_ICON,
          title: 'Access a trusted network',
          description: 'We employ brokers and sourcing teams around the world to vet the suppliers in our network.',
        },
        {
          icon: CUSTOM_PROJECTS_ICON,
          title: 'Create custom projects',
          description: 'Post your custom projects to our marketplace via a RFP and get connected with suppliers.',
        },
        {
          icon: FIND_SUPPLIER_ICON,
          title: 'Find the right supplier for you',
          description: 'Use our marketplace with categories to find factories and freelancers.',
        },
        {
          icon: PROJECT_MANAGEMENT_ICON,
          title: 'Project management tools',
          description: 'Centralized communication, POs, and assets. The tools you need to run your business.',
        },
      ],
    },
    {
      type: PlatformPageSectionType.CALL_TO_ACTION,
      preTitle: 'GO DIRECT TO FACTORY',
      preTitleColor: '#fff',
      title: 'Never pay agents or brokers to work with top factories.',
      titleColor: '#fff',
      description: "We've found the best factories in the world so you don't have to spend the time finding them.",
      descriptionColor: '#fff',
      bgColor: '#ff5c3c',
      buttons: [{ btnText: 'Sign up for free' }],
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Sourcing & Production',
      preTitleColor: '#ff5c3c',
      title: 'Access a trusted production network',
      titleColor: '#000000',
      description:
        'We employ brokers and sourcing teams around the world to bring you the best suppliers that work with top brands.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#FFFFFF',
      buttons: [{ btnText: 'View the Pietra network' }],
      image: `${STATIC_FILE_URL}/57b753565b8ee5f1635eb11f507fb016.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Sourcing & Production',
      preTitleColor: '#ff5c3c',
      title: 'Suppliers to create your one-of-a-kind products',
      titleColor: '#000000',
      description:
        'Submit a custom project via a RFP and reach 1300+  suppliers in minutes. Work with them to bring your ideas to life.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#f9f2f1',
      buttons: [{ btnText: 'Find your suppliers today' }],
      image: `${STATIC_FILE_URL}/857136187aad235b09a787afa4ff9f91.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Sourcing & Production',
      preTitleColor: '#ff5c3c',
      title: 'Easily find factories and freelancers that fit your needs',
      titleColor: '#000000',
      description: 'Use our marketplace to search through 500,000+ products and service offerings that fit your needs.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      buttons: [{ btnText: 'Search on the Pietra' }],
      bgColor: '#FFFFFF',
      image: `${STATIC_FILE_URL}/08a0c605835306db6ee79bbccafde519.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Sourcing & Production',
      preTitleColor: '#ff5c3c',
      title: 'Centralized comms, POs, and assets',
      titleColor: '#000000',
      description:
        "Pietra consolidates all of your supplier communications, POs, tracking numbers, and more so you don't have to dig through your email.",
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#f9f2f1',
      buttons: [{ btnText: 'Try Pietra Now' }],
      image: `${STATIC_FILE_URL}/f2ebaacacfd8436f499ffacc620ab580.png`,
    },
  ],
};

export const LOGISTICS_AND_FULFILLMENT_CONTENT: IPlatformPage = {
  type: PlatformPageType.LOGISTICS_AND_FULFILLMENT,
  title: 'Fulfillment without breaking the bank',
  hero: {
    colors: {
      text: '#000',
    },
    class: 'logistics',
    btnText: 'Lock in rates now',
    btnHubUrl: '/platform/logistics-and-fulfillment/signup',
    secondaryBtn: {
      text: 'View rate card',
      externalUrl: 'https://drive.google.com/file/d/16d42tQIbSp9Wm4gfhk6uKo3G0bVJ4yWl/view?pli=1',
    },
    description:
      'A modern 3PL designed to help you grow your DTC, wholesale, and retail business with $1.50/order unlimited picks that saves you on average 60% on costs.',
    isHeroOverlayShown: true,
    showSignUp: true,
  },
  sections: [
    {
      type: PlatformPageSectionType.TEXT_GRID,
      preTitle: 'Logistics & Fulfillment',
      preTitleColor: '#504738',
      title: "Use Pietra's private 3PL only available to members. ",
      titleColor: '#000000',
      description:
        'Organize shipments directly from the factory floor, order kitting, photography, and save between 30-50% of fulfillment and shipping.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#ffffff',
      buttons: [{ btnText: 'Sign Up for Free' }],
      textGridItems: [
        {
          icon: SHIPPING_ICON,
          title: 'Ship direct from manufacturer',
          description:
            "Pietra makes it easy to ship directly from the factory to our facility. We'll organize the shipment for you.",
        },
        {
          icon: FULFILLMENT_ICON,
          title: 'High touch order fulfillment',
          description:
            'Reach your customers around the world with 2 day order fulfillment and an easy to  use fulfillment dashboard.',
        },
        {
          icon: KITTING_ICON,
          title: 'Kitting & product assembly',
          description: "Ship all your components to Pietra and we'll assemble your finished products",
        },
        {
          icon: WHOLESALE_ICON,
          title: 'Wholesale order fulfillment',
          description:
            'Grow your business into wholesale and retail locations and let Pietra manage your fulfillment operation.',
        },
      ],
    },
    {
      type: PlatformPageSectionType.CALL_TO_ACTION,
      preTitle: 'Designed for entrepreneurs',
      preTitleColor: '#000000',
      title: 'Set your business up for success with a private 3PL',
      titleColor: '#000000',
      description: 'Save between 30-50% on fulfillment and shipping with your Pietra membership.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#F9E070',
      buttons: [{ btnText: 'Sign Up for Free' }],
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Logistics & Fulfillment',
      preTitleColor: '#504738',
      title: 'Ship direct from your factory',
      titleColor: '#000000',
      description:
        "We'll organize a shipment from your factory directly to Pietra's facility, so you don't have to worry about a thing.",
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#FFFFFF',
      buttons: [{ btnText: 'Ship to Pietra now' }],
      image: `${STATIC_FILE_URL}/455844173c4fe9f55aa33de90b1a1511.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Logistics & Fulfillment',
      preTitleColor: '#504738',
      title: 'High touch order fulfillment',
      titleColor: '#000000',
      description:
        "We'll rapidly fulfill your customers orders as well as any influencer gifts or special shipments. Access an easy to use dashboard to check order statuses at any time.",
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#FFFBE7',
      buttons: [{ btnText: 'Set Up Fulfillment on Pietra' }],
      image: `${STATIC_FILE_URL}/7ae69c0efed016f25f787c9a7b761ac9.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Logistics & Fulfillment',
      preTitleColor: '#504738',
      title: 'Kitting & product assembly',
      titleColor: '#000000',
      description:
        "Ship components from multiple factories around the world and we'll kit your final products before they are shipped to your customers.",
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#FFFFFF',
      buttons: [{ btnText: 'Kit With Pietra Now' }],
      image: `${STATIC_FILE_URL}/8958c12408a5e9f53ec680eb890eda8f.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Logistics & Fulfillment',
      preTitleColor: '#504738',
      title: 'Wholesale order fulfillment',
      titleColor: '#000000',
      description:
        'Grow your business through wholesale opportunities and trust Pietra to package and ship all your orders. ',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#FFFBE7',
      buttons: [{ btnText: 'Set Up Wholesale Fulfillment' }],
      image: `${STATIC_FILE_URL}/c033bc02d1026ef056b2527503057a51.png`,
    },
  ],
};

export const PRINT_ON_DEMAND_CONTENT: IPlatformPage = {
  type: PlatformPageType.PRINT_ON_DEMAND,
  title: 'Start your online business for free.',
  hero: {
    colors: {
      text: '#fff',
      background: 'linear-gradient(to bottom, #57AD6A 65%,  rgba(87, 173, 106, 1.0) 100%)',
    },
    class: 'print-on-demand',
    bgImage: `${STATIC_FILE_URL}/629b6b64e0bb96ddc4062f814876a0e7.webp`,
    bgImageAltText: 'A FREE print on demand tool from Pietra. Compare to Fourth Wall, Custom Ink, Printify',
    secondaryImage: `${STATIC_FILE_URL}/c2728cbdfbbd1adf0cce5f5441386a65.webp`,
    btnText: 'Start designing',
    description:
      'No setup costs and no inventory costs. Just get a free Pietra membership, upload your designs, and start selling online.',
    isHeroOverlayShown: true,
    showSignUp: true,
  },
  sections: [
    {
      type: PlatformPageSectionType.TEXT_GRID,
      preTitle: 'Print On Demand',
      preTitleColor: '#57AD6A',
      title: 'Start your brand in 5 minutes.',
      titleColor: '#000000',
      description: "Merchandise products and design your own storefront. All part of Pietra's Free Membership.",
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#ffffff',
      buttons: [{ btnText: 'Start for free' }],
      textGridItems: [
        {
          icon: '/images/print-on-demand/best-pricing.svg',
          title: 'Great for beginners',
          description: 'Easy to use tools to upload designs and set up a storefront.',
        },
        {
          icon: '/images/print-on-demand/direct-to-garment.svg',
          title: 'No upfront cost',
          description: 'Test ideas and never hold inventory. Products are produced after you make a sale.',
        },
        {
          icon: '/images/print-on-demand/inventory-free.svg',
          title: 'Best prices',
          description: "We've partnered with Printful to get you the lowest cost to maximize your profit.",
        },
        {
          icon: '/images/print-on-demand/lowest-cost.svg',
          title: 'Start selling instantly',
          description: 'No approvals or extra steps. Make a sale and make money today.',
        },
      ],
    },
    {
      type: PlatformPageSectionType.INSTRUCTIONAL_VIDEOS,
      preTitle: 'Print on Demand',
      preTitleColor: '#57AD6A',
      title: 'How does it work?',
      titleColor: '#000000',
      description: 'Watch these short instructional videos on Print on Demand',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#FFF',
      instructionalVideos: [
        {
          videoUrl: 'https://vimeo.com/846033917?share=copy',
          thumbnail: `${STATIC_FILE_URL}/2ceda6484b0f004613d801cbaf7d3731.png`,
          altText: "Exploring Pietra's Print-on-Demand Tools",
        },
        {
          videoUrl: 'https://vimeo.com/846034252?share=copy',
          thumbnail: `${STATIC_FILE_URL}/27941b9f29d452e0c438db799aee5eac.png`,
          altText: 'Setting Up Your Shop and Taking Orders with Pietra',
        },
      ],
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Print on Demand',
      preTitleColor: '#57AD6A',
      title: 'Design beautiful products for your brand that will sell.',
      titleColor: '#000000',
      description: 'Upload your branding and start creating beautiful products to get the word out.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#EBF5ED',
      buttons: [{ btnText: 'Try it for free' }],
      image: `${STATIC_FILE_URL}/3200b2284c0d3cdd022e449313d5b5b1.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Print on Demand',
      preTitleColor: '#57AD6A',
      title: 'Ship to influencers, create a merch shop, and more.',
      titleColor: '#000000',
      description:
        'Ship it directly to your office or set up your super simple e-commerce page to start selling your custom designed product with no upfront cost.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#FFF',
      buttons: [{ btnText: 'Start for free' }],
      image: `${STATIC_FILE_URL}/2628e19d73b8b6f5f456fd93acabee15.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Print on Demand',
      preTitleColor: '#57AD6A',
      title: 'High quality embroidery and printing.',
      titleColor: '#000000',
      description:
        'Create products that match the quality of your business. All of the products are vetted by Pietra specialists.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#EBF5ED',
      buttons: [{ btnText: 'Start for free' }],
      image: `${STATIC_FILE_URL}/da6099592909d3f8730f6823bbc6b347.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Print on Demand',
      preTitleColor: '#57AD6A',
      title: 'Get your product delivered within days.',
      titleColor: '#000000',
      description: 'With upfront sampling times and costs, see your product on the front doorstep within a week.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#FFF',
      buttons: [{ btnText: 'Start for free' }],
      image: `${STATIC_FILE_URL}/a5faed426565ab8cc8814ff7c05e90f5.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Print on Demand',
      preTitleColor: '#57AD6A',
      title: 'Cheaper than the alternatives.',
      titleColor: '#000000',
      description: 'At least 10% lower prices with no setup costs or hidden fees.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#EBF5ED',
      buttons: [{ btnText: 'Start for free' }],
      image: `${STATIC_FILE_URL}/02d23ae7b75952bc5c847fbe243eb3db.webp`,
    },
  ],
};

export const PRIVATE_WHOLESALE_CONTENT: IPlatformPage = {
  type: PlatformPageType.PRIVATE_WHOLESALE,
  title: 'Save money with a Private Wholesale site for your brand',
  hero: {
    colors: {
      text: '#fff',
      background: 'linear-gradient(to bottom, #57AD6A 65%,  rgba(87, 173, 106, 1.0) 100%)',
    },
    class: 'private-wholesale',
    bgImage: `${STATIC_FILE_URL}/e9d3cd6833fcd7013e65798bc9dcfe45.png`,
    btnText: 'Sign up for Pietra',
    secondaryBtn: {
      text: 'Already a member? Try it.',
      creatorHubUrl: '/manage-shop#wholesale',
    },
    description: "Set up a FREE site and use Pietra's 3PL to handle all your B2B wholesale and fulfillment needs.",
    isHeroOverlayShown: true,
    showSignUp: true,
  },
  sections: [
    {
      type: PlatformPageSectionType.TEXT_GRID,
      preTitle: 'Private Wholesale',
      preTitleColor: '#57AD6A',
      title: 'A storefront for your top retailers.',
      titleColor: '#000000',
      description: 'Set up your own private wholesale site and let Pietra handle all the shipping and fulfillment.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#ffffff',
      buttons: [{ btnText: 'Set up your site' }],
      textGridItems: [
        {
          icon: '/images/private-wholesale/storefront.svg',
          title: 'Branded storefronts',
          description: 'Make it your own with a logo, description, and short bio.',
        },
        {
          icon: '/images/private-wholesale/b2b.svg',
          title: 'Managed B2B fulfillment',
          description: "Send your B2B inventory to Pietra and we'll package and ship to your retailers.",
        },
        {
          icon: '/images/private-wholesale/password-protected.svg',
          title: 'Password protected',
          description: 'Give your top retailers access to special deals with a special access code.',
        },
        {
          icon: '/images/private-wholesale/no-fees.svg',
          title: 'No marketplace fees',
          description:
            'Pay standard e-commerce and payment processing rates on your website. Avoid costly marketplace fees.',
        },
      ],
    },
    {
      type: PlatformPageSectionType.INSTRUCTIONAL_VIDEOS,
      preTitle: 'Private Wholesale',
      preTitleColor: '#57AD6A',
      title: 'How Pietra helps you save on wholesale',
      titleColor: '#000000',
      description: 'Watch these short instructional videos on Private Wholesale',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#FFF',
      instructionalVideos: [
        {
          videoUrl: 'https://vimeo.com/846036025?share=copy',
          thumbnail: `${STATIC_FILE_URL}/971c0e93fc4c567f747913c07eac5483.png`,
          altText: 'Setting Up Your Private Wholesale Site on Pietra',
        },
        {
          videoUrl: 'https://vimeo.com/846036332?share=copy',
          thumbnail: `${STATIC_FILE_URL}/c3fd77ba500286b84b84c0fe2e0aa93a.png`,
          altText: 'Setting Up Custom Rates and MOQs for Your Wholesale Site',
        },
        {
          videoUrl: 'https://vimeo.com/846036457?share=copy',
          thumbnail: `${STATIC_FILE_URL}/75e8e28e724f97c8641c62c96d8944fe.png`,
          altText: 'How Retailers Access Private Wholesale Sites',
        },
      ],
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Private Wholesale',
      preTitleColor: '#57AD6A',
      title: 'Stop paying marketplace fees for repeat purchases.',
      titleColor: '#000000',
      description: 'Find retailers on marketplaces, then share your private wholesale website link to save money.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#EBF5ED',
      buttons: [{ btnText: 'Set up private wholesale' }],
      image: `${STATIC_FILE_URL}/806c026f5f33ee15ff26a5bfc0dc79d0.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Private Wholesale',
      preTitleColor: '#57AD6A',
      title: 'Give better rates to your top retailers.',
      titleColor: '#000000',
      description: "Set pricing, minimums, and special offers that aren't inflated due to marketplace fees.",
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#FFF',
      buttons: [{ btnText: 'Set up private wholesale' }],
      image: `${STATIC_FILE_URL}/07b7b4f2059d9456de75ed0acd13615e.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Private Wholesale',
      preTitleColor: '#57AD6A',
      title: 'Automate your wholesale business.',
      titleColor: '#000000',
      description:
        "Create a storefront, use Pietra's 3PL, and let your top retailers come directly to you for the best buying experience.",
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#EBF5ED',
      buttons: [{ btnText: 'Set up private wholesale' }],
      image: `${STATIC_FILE_URL}/051825437d2cdc1172fe7b510577441e.png`,
    },
  ],
};

export const AFFILIATE_MARKETING_HEADER_CONTENT: IPlatformHeaderContent = {
  heroImage: {
    url: `${STATIC_FILE_URL}/79a6e58f8864624f5213a6236a8179f4.png`,
    width: 1024,
    height: 433,
    mobileWidth: 320,
    mobileHeight: 135,
  },
  bookDemoPath: '/platform/affiliate-marketing/signup',
  platformName: 'Influencer Marketing',
  title: 'Accelerate your influencer marketing programs',
  description: 'Build a powerful influencer marketing program to grow sales, track performance and payouts',
  backgroundColor: '#FF5C3C',
  color: '#FFF',
  companyLogos: {
    title: 'Compare to',
    logos: [
      { name: 'UpPromote', url: `${STATIC_FILE_URL}/f26317f63cb1160a8af26379cd88e0fc.svg`, width: 94, height: 24 },
      { name: 'Refersion', url: `${STATIC_FILE_URL}/1155c321d1eb809b770016661cf419d8.svg`, width: 133, height: 24 },
    ],
  },
};

export const AFFILIATE_MARKETING_CONTENT: IPlatformPage = {
  type: PlatformPageType.AFFILIATE_MARKETING,
  title: 'Accelerate your influencer marketing programs',
  sections: [
    {
      type: PlatformPageSectionType.TEXT_GRID,
      preTitle: 'Affiliate Marketing',
      preTitleColor: '#FF5C3C',
      title: 'Manage your influencer campaigns in one place.',
      titleColor: '#000000',
      description: '',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#ffffff',
      buttons: [{ btnText: 'Free with membership' }],
      textGridItems: [
        {
          icon: '/images/affiliate-marketing/offers.svg',
          title: 'Offers',
          description:
            'Create campaigns based on different monetary reimbursement types. You can then assign your affiliates to an offer.',
        },
        {
          icon: '/images/affiliate-marketing/affiliate.svg',
          title: 'Affiliate',
          description: 'Each affiliate gets a referral link so you can track sales with pinpoint precision.',
        },
        {
          icon: '/images/affiliate-marketing/orders.svg',
          title: 'Orders',
          description: 'Once your affiliates help you make a sale, you can approve them to be paid out.',
        },
        {
          icon: '/images/affiliate-marketing/payments.svg',
          title: 'Payments',
          description: 'Choose when to pay out your affiliates through PayPal.',
        },
      ],
    },
    {
      type: PlatformPageSectionType.INSTRUCTIONAL_VIDEOS,
      preTitle: 'Affiliate Marketing',
      preTitleColor: '#FF5C3C',
      title: 'How does it work?',
      titleColor: '#000000',
      description: 'Watch these short instructional videos on Affiliate Marketing',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#FFF',
      instructionalVideos: [
        {
          videoUrl: 'https://vimeo.com/846037186?share=copy',
          thumbnail: `${STATIC_FILE_URL}/ef438741f8bb392f5f854ed29037adb7.png`,
          altText: 'Accelerate Your Influencer and Affiliate Programs with Pietra!',
        },
        {
          videoUrl: 'https://vimeo.com/846037329?share=copy',
          thumbnail: `${STATIC_FILE_URL}/a91be89f82605d5ae14a41ad50532982.png`,
          altText: 'Setting Up Affiliate Marketing Program and Registering Affiliates',
        },
        {
          videoUrl: 'https://vimeo.com/846039730?share=copy',
          thumbnail: `${STATIC_FILE_URL}/f6a4a3ac5d05320ca286d097f453ea4f.png`,
          altText: 'Track Your Commissions as an Affiliate',
        },
      ],
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Affiliate Marketing',
      preTitleColor: '#000',
      title: 'Invite influencers & affiliates.',
      titleColor: '#000000',
      description: 'Build your team of affiliates to help your brand grow sales and make more money.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#F9F2F1',
      buttons: [{ btnText: 'Set up affiliate marketing' }],
      image: `${STATIC_FILE_URL}/b33bf4a551d241063cbdf10649f37d62.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Affiliate Marketing',
      preTitleColor: '#000',
      title: 'Create custom offers for each affiliate.',
      titleColor: '#000000',
      description: 'Create custom URLs for your affiliates to sign up based on your varied offers.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#FFF',
      buttons: [{ btnText: 'Set up affiliate marketing' }],
      image: `${STATIC_FILE_URL}/0a9b7d819280261a9f7274ad70c9075f.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Affiliate Marketing',
      preTitleColor: '#000',
      title: 'Manage your offers and approve payouts.',
      titleColor: '#000000',
      description: 'Review every conversion recorded and ensure your affiliates are performing to your standards.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#F9F2F1',
      buttons: [{ btnText: 'Set up affiliate marketing' }],
      image: `${STATIC_FILE_URL}/2ffb941b43d486e40c6e7371a44e5183.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Affiliate Marketing',
      preTitleColor: '#000',
      title: 'Reward your affiliates when they grow your sales.',
      titleColor: '#000000',
      description: 'Pay your affiliates with PayPal and easily add bonuses.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#FFF',
      buttons: [{ btnText: 'Free with membership' }],
      image: `${STATIC_FILE_URL}/6cc9f1bdd2766e3205d7221ffb69d9de.png`,
    },
    {
      type: PlatformPageSectionType.CUSTOMER_REVIEWS,
      title: 'Hear from our customers',
      titleColor: '#000000',
      description: 'More than 5000 customers trust Pietra to accelerate their creative workflows',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#ffffff',
      reviewItems: [
        {
          text: "Pietra's affiliate marketing program was essential in allowing us to see everything in one place and scale our affiliate program to candy lovers everywhere! ",
          stars: 5,
          avatar: '/images/customer-reviews/avatar-kasey.png',
          name: 'Kasey S',
          org: 'Bad Mouth',
        },
        {
          text: 'Pietra gives all my ambassadors their own portal to track their referrals. It makes it very easy to grow my ambassador program and pay them out. ',
          stars: 5,
          avatar: '/images/customer-reviews/avatar2.png',
          name: 'Aaron A',
          org: 'Bleeker Street Designs',
        },
      ],
    },
  ],
};

export const RETURNS_CONTENT: IPlatformPage = {
  type: PlatformPageType.RETURNS,
  title: 'Returns automation for positive returns',
  hero: {
    colors: {
      text: '#000',
      background: 'linear-gradient(to bottom, #A9BEF2 65%,  rgba(160, 190, 242, 1.0) 100%)',
    },
    class: 'returns',
    bgImage: `${STATIC_FILE_URL}/4e9a3f289a8b7bd51ffcdc8d4cf0be16.png`,
    bgImageAltText: 'Returns automation for positive returns. Compare to Aftership, Returnly',
    secondaryImage: `${STATIC_FILE_URL}/8282b305ea7b68d9785c24c6388353cd.png`,
    btnText: 'Sign up for Pietra',
    secondaryBtn: {
      text: 'Already a member? Try it.',
      creatorHubUrl: '/return-settings',
    },
    description: 'Save on shipping costs, and decrease refunds. Included FREE with your membership.',
    isHeroOverlayShown: true,
    showSignUp: true,
  },
  sections: [
    {
      type: PlatformPageSectionType.TEXT_GRID,
      preTitle: 'Pietra Returns',
      preTitleColor: '#3C5CAB',
      title: 'Deliver a better returns experience',
      titleColor: '#000000',
      description: 'A complete returns management experience with all the features you need',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#ffffff',
      buttons: [{ btnText: 'Set up Returns now' }],
      textGridItems: [
        {
          icon: '/images/returns/returns-url.svg',
          title: 'Custom returns URL',
          description: 'Provide your customers with a unique URL to create and track return status.',
        },
        {
          icon: '/images/returns/return-window.svg',
          title: 'Return window',
          description: 'Set a custom return a window from the date of purchase.',
        },
        {
          icon: '/images/returns/item-specific.svg',
          title: 'Item-specific returns',
          description: 'Determine which items in your catalog customers can return.',
        },
        {
          icon: '/images/returns/return-shipping.svg',
          title: 'Return shipping costs',
          description: 'Select whether your customer will pay for the return shipping.',
        },
      ],
    },
    {
      type: PlatformPageSectionType.INSTRUCTIONAL_VIDEOS,
      preTitle: 'Pietra Returns',
      preTitleColor: '#3C5CAB',
      title: 'How does it work?',
      titleColor: '#000000',
      description: 'Watch these short instructional videos on Automated Pietra Returns',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#FFF',
      instructionalVideos: [
        {
          videoUrl: 'https://vimeo.com/846035505?share=copy',
          thumbnail: `${STATIC_FILE_URL}/cf2625ffc6cfed2d3bfd961e725ddb68.png`,
          altText: 'Automating Returns in Pietra',
        },
        {
          videoUrl: 'https://vimeo.com/846035669?share=copy',
          thumbnail: `${STATIC_FILE_URL}/513f1a29939483a7292c927707d7072e.png`,
          altText: 'Streamlining Returns with Pietra',
        },
      ],
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Pietra Returns',
      preTitleColor: '#3C5CAB',
      title: 'Improve your returns experience',
      titleColor: '#000000',
      description: 'Create a thoughtful return experience to increase brand engagement and drive future repurchases.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#EEF4FF',
      buttons: [{ btnText: 'Set up Returns now' }],
      image: `${STATIC_FILE_URL}/a83c5520c0e41bf34b97d1d217dad9e5.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Pietra Returns',
      preTitleColor: '#3C5CAB',
      title: 'Reduce operational costs',
      titleColor: '#000000',
      description:
        'Recapture returned revenue with exchanges, incentives, and partial refunds. A 50% reduction in returns processing time.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#fff',
      buttons: [{ btnText: 'Set up Returns now' }],
      image: `${STATIC_FILE_URL}/21bb2e23970c6a6b26fff7a9c489f04a.png`,
    },
  ],
};

export const SMS_MARKETING_CONTENT: IPlatformPage = {
  type: PlatformPageType.AFFILIATE_MARKETING,
  title: "Don't pay extra for SMS marketing",
  sections: [
    {
      type: PlatformPageSectionType.TEXT_GRID,
      preTitle: 'All in one',
      preTitleColor: '#FF5C3C',
      title: "SMS marketing made so simple. It's a no brainer.",
      titleColor: '#000000',
      description: '',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#ffffff',
      buttons: [
        { btnText: 'Get started for free', btnClass: 'black' },
        { btnText: 'Book a demo', onClick: (router: NextRouter) => router.push('/platform/sms-marketing/signup') },
      ],
      textGridItems: [
        {
          icon: '/images/sms-marketing/texts.svg',
          title: 'AI-suggested texts',
          description: "Generate SMS copy at light speed with a custom voice that's unique to your brand.",
        },
        {
          icon: '/images/sms-marketing/audiences.svg',
          title: 'Smart audiences',
          description: 'Pietra imports your Shopify data to suggest the best customers for targeting.',
        },
        {
          icon: '/images/sms-marketing/shopify.svg',
          title: 'Promo codes',
          description: 'Automatically create Shopify codes for your campaign.',
        },
        {
          icon: '/images/sms-marketing/url.svg',
          title: 'Click tracking',
          description: 'Pietra tracks all your URLs for insights and actions.',
        },
      ],
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Personal Copywriter',
      preTitleColor: '#FF5C3C',
      title: 'Turn texts into dollars with the help of AI.',
      titleColor: '#000000',
      description: 'Create personalized and engaging texts in seconds that are proven to convert to orders.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#F9F2F1',
      buttons: [
        { btnText: 'Get started for free', btnClass: 'black' },
        { btnText: 'Book a demo', onClick: (router: NextRouter) => router.push('/platform/sms-marketing/signup') },
      ],
      image: `${STATIC_FILE_URL}/7e22b06eabca1532473d0162ce2cb309.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Custom Targeting',
      preTitleColor: '#FF5C3C',
      title: 'Reach the right customer at the right time.',
      titleColor: '#000000',
      description: 'Personalized messaging and offers to drive more new and repeat orders.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#FFF',
      buttons: [
        { btnText: 'Get started for free', btnClass: 'black' },
        { btnText: 'Book a demo', onClick: (router: NextRouter) => router.push('/platform/sms-marketing/signup') },
      ],
      image: `${STATIC_FILE_URL}/2a1ffba0cf5345a2d1789c020e02eb5b.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Actionable Reports',
      preTitleColor: '#FF5C3C',
      title: 'From insights to actions.',
      titleColor: '#000000',
      description:
        "With Pietra, you know exactly what's driving results and can spend additional dollars where it can make a difference.",
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#F9F2F1',
      buttons: [
        { btnText: 'Get started for free', btnClass: 'black' },
        { btnText: 'Book a demo', onClick: (router: NextRouter) => router.push('/platform/sms-marketing/signup') },
      ],
      image: `${STATIC_FILE_URL}/e63c04f54334b8712238072fb8003870.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Fully Compliant',
      preTitleColor: '#FF5C3C',
      title: 'Compliance safeguards for lists and high deliverability.',
      titleColor: '#000000',
      description:
        "Pietra demystifies SMS regulations so you can grow your audience and convert them. Opt in consent, opt out keywords, quiet hours—it's all built in.",
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#FFF',
      buttons: [
        { btnText: 'Get started for free', btnClass: 'black' },
        { btnText: 'Book a demo', onClick: (router: NextRouter) => router.push('/platform/sms-marketing/signup') },
      ],
      image: `${STATIC_FILE_URL}/0b3577921cc2d1a8488a74ac8909b195.png`,
    },
    {
      type: PlatformPageSectionType.CUSTOMER_REVIEWS,
      title: 'Hear from our customers',
      titleColor: '#000000',
      description: 'More than 5000 customers trust Pietra to accelerate their creative workflows',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#ffffff',
      reviewItems: [
        {
          text: 'Shifting from my current SMS marketing solution to Pietra was so seamless. The product also offered better reporting and the first campaign resulted in a higher CTR than my previous solution at 38% the cost!',
          stars: 5,
          avatar: '/images/customer-reviews/avatar-lindsay.png',
          name: 'Lindsay K',
          org: 'Lively Ghosts',
        },
        {
          text: 'Using the new sms marketing feature has been so simple & easy! The process of set up to actively sending out messages and how it automated syncs as new customers sign up has been a huge help in growing my new business.',
          stars: 5,
          avatar: '/images/customer-reviews/avatar-kourtney.png',
          name: 'Kourtney K',
          org: 'KKActive',
        },
      ],
    },
  ],
};

export const ANALYTICS_HEADER_CONTENT: IPlatformHeaderContent = {
  heroImage: {
    url: `${STATIC_FILE_URL}/7e5fcc8b20d6796da3ef0a51e4cfdfd9.png`,
    width: 1450,
    height: 710,
    mobileWidth: 1450,
    mobileHeight: 710,
  },
  bookDemoPath: '/platform/analytics/signup',
  platformName: 'Analytics',
  title: 'Customer insights for your Shopify brand.',
  description:
    "Save thousands a month and don't pay more as you grow. Connect your storefront and get free insights with your Pietra membership.",
  backgroundColor: '#88B3E7',
  color: '#fff',
  companyLogos: {
    title: 'Works with',
    logos: [
      { name: 'Shopify', url: `${STATIC_FILE_URL}/5f74afc7741a25b5e891c0c6228de8da.svg`, width: 158, height: 45 },
      { name: 'WooCommerce', url: `${STATIC_FILE_URL}/4f2e998f11299e712b7880fb9de8f9e1.svg`, width: 230, height: 46 },
      { name: 'Wix', url: `${STATIC_FILE_URL}/cd2d1bb484ea82b001fc519b4c209682.svg`, width: 82, height: 32 },
    ],
  },
  popularUseCases: {
    title: { text: 'Popular use cases', color: '#FFF' },
    cards: [
      {
        color: '#526EB2',
        pill: '+26% repeat purchase',
        title: 'Loyalty offer',
        desc: "Send customers an order follow up discount to say thanks. Includes a follow up reminder a day later if they haven't used it.",
        icon: blue1_loyalty_offer,
        button: {
          text: 'Get set up',
          color: '#526EB2',
          bgColor: '#f7f9ff',
          onClick: (router: NextRouter) => router.push('/platform/analytics/signup'),
        },
      },
      {
        color: '#526EB2',
        pill: '+16% more repeat purchases',
        title: 'Optimize your customer acquisition',
        desc: 'Find out which channels and campaigns your marketing budget is well invested in. Gain insight into all qualitative metrics and optimize your customer acquisition for maximum long-term success.',
        icon: blue1_email,
        button: {
          text: 'Get set up',
          color: '#526EB2',
          bgColor: '#f7f9ff',
          onClick: (router: NextRouter) => router.push('/platform/analytics/signup'),
        },
      },
      {
        color: '#526EB2',
        pill: '+34% net revenue increase',
        title: 'Sell your products better',
        desc: 'Improve sales and profit margins and identify products that lead to maximum customer lifetime value. Feed this information back to all teams to optimize production, marketing, and sales.',
        icon: blue1_shopping_bags,
        button: {
          text: 'Get set up',
          color: '#526EB2',
          bgColor: '#f7f9ff',
          onClick: (router: NextRouter) => router.push('/platform/analytics/signup'),
        },
      },
    ],
  },
};

export const ANALYTICS_CONTENT: IPlatformPage = {
  type: PlatformPageType.ANALYTICS,
  title: 'Customer insights for your Shopify brand',
  sections: [
    {
      type: PlatformPageSectionType.TEXT_GRID,
      preTitle: 'Pietra Analytics',
      preTitleColor: '#000',
      title: 'Analytics to grow your brand',
      titleColor: '#000000',
      description: '',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#ffffff',
      buttons: [
        {
          btnText: 'Get started for free',
          btnClass: 'black',
          onClick: () => {
            (document.querySelector('.hero-btn') as HTMLElement)?.click();
          },
        },
        { btnText: 'Book a demo', onClick: (router: NextRouter) => router.push('/platform/analytics/signup') },
      ],
      textGridItems: [
        {
          icon: ECOMMERCE_ICON,
          title: 'Shopify, WooCommerce, & Wix support',
          description: 'Connect to your preferred e-commerce platform',
        },
        {
          icon: ODOMETER_ICON,
          title: 'Visibility into your brand',
          description: 'See your data across customers, orders, products, and marketing',
        },
        {
          icon: LIGHTBULB_ICON,
          title: 'Get insights on your customers',
          description: 'Understand common trends and act on them quickly',
        },
        {
          icon: GROW_SALES_ICON,
          title: 'Grow your brand',
          description: 'Identify marketing and sales gaps in your e-commerce brand',
        },
      ],
    },
    {
      type: PlatformPageSectionType.IMAGE_2x2_GRID,
      bgColor: '#F3F7FF',
      fullGridImages: [
        {
          title: 'For customers',
          description: 'Analytics to prevent customer churn and optimize long-term LTV of your buyers.',
          imgAlt: 'Analytics to prevent customer churn and optimize long-term LTV of your buyers.',
          imgSrc: `${STATIC_FILE_URL}/1e73e58798f8fe3e1ed7805016ef6410.png`,
        },
        {
          title: 'For orders',
          description: 'Analytics to optimize order frequency and AOV.',
          imgAlt: 'Analytics to optimize order frequency and AOV.',
          imgSrc: `${STATIC_FILE_URL}/ca013eb98e28a61599955417fdd125ef.png`,
        },
        {
          title: 'For products',
          description: 'Analytics to push top sellers and introduce new products.',
          imgAlt: 'Analytics to increase to push top sellers and introduce new products',
          imgSrc: `${STATIC_FILE_URL}/1152dc8cebc3c1e8ea371d81ea459a82.png`,
        },
        {
          title: 'For marketing',
          description: 'Analytics to understand your marketing channels performance.',
          imgAlt: 'Analytics to understand your marketing channels performance',
          imgSrc: `${STATIC_FILE_URL}/226a360c41685ac6ddda7f3237c8191e.png`,
        },
      ],
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Pietra Analytics',
      preTitleColor: '#000',
      title: 'Create a 360 profile of your customer',
      titleColor: '#000000',
      description: 'Get all of the information about your customers in one single place to take action.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#FFF',
      image: `${STATIC_FILE_URL}/0a78c16eef69e82376ccfa3eb6f59ae3.png`,
      buttons: [
        {
          btnText: 'Get started for free',
          btnClass: 'black',
          onClick: () => {
            (document.querySelector('.hero-btn') as HTMLElement)?.click();
          },
        },
        { btnText: 'Book a demo', onClick: (router: NextRouter) => router.push('/platform/analytics/signup') },
      ],
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Pietra Analytics',
      preTitleColor: '#000',
      title: 'Understanding insights on your customer',
      titleColor: '#000000',
      description: 'Identify patterns that can help you increase retention and LTV of your customers.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#F3F7FF',
      image: `${STATIC_FILE_URL}/a0cf1b92f9cb79b38b84ea29a097cf46.png`,
      buttons: [
        {
          btnText: 'Get started for free',
          btnClass: 'black',
          onClick: () => {
            (document.querySelector('.hero-btn') as HTMLElement)?.click();
          },
        },
        { btnText: 'Book a demo', onClick: (router: NextRouter) => router.push('/platform/analytics/signup') },
      ],
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      preTitle: 'Pietra Analytics',
      preTitleColor: '#000',
      title: 'Grow your brand',
      titleColor: '#000000',
      description: 'Take action on your insights by implementing new sales and marketing tactics.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#FFF',
      image: `${STATIC_FILE_URL}/6d423b7db63f9528f4105af95aade4c1.png`,
      buttons: [
        {
          btnText: 'Pietra SMS Marketing',
          btnHubUrl: '/platform/sms-marketing',
        },
      ],
    },
    {
      type: PlatformPageSectionType.TEXT_CALENDAR,
      calendlyUrl: ANALYTICS_CALENDLY,
      preTitle: 'Pietra Analytics',
      preTitleColor: '#000',
      title: 'See how you can grow your brand today',
      titleColor: '#000000',
      description:
        'Schedule a demo to explore Pietra Analytics with an expert and see how you can grow your brand today.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#FFF',
    },
    {
      type: PlatformPageSectionType.CUSTOMER_REVIEWS,
      title: 'Hear from our customers',
      titleColor: '#000000',
      description: 'More than 5000 customers trust Pietra to accelerate their creative workflows',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#ffffff',
      reviewItems: [
        {
          text: 'I have access to charts that cover customers, orders, products, and marketing just by connecting my Shopify. It helped my DTC brand identify which products I should cross-sell, when to run my discount campaigns, and more! ',
          stars: 5,
          avatar: '/images/customer-reviews/avatar-oren-s.png',
          name: 'Oren S',
          org: 'Gel blasters',
        },
        {
          text: 'Pietra is offering analytics software that costs hundreds of dollars from other vendors. With just one monthly membership, my DTC brand can access 50+ graphs. Plus, Pietra is adding more graphs monthly.',
          stars: 5,
          avatar: '/images/customer-reviews/avatar1.png',
          name: 'Zach G',
          org: 'Tezzy Tees',
        },
      ],
    },
  ],
};

export const SMS_MARKETING_HEADER_CONTENT: IPlatformHeaderContent = {
  heroImage: {
    url: `${STATIC_FILE_URL}/de72bad61ffc22953dc8469fad2f6c07.png`,
    width: 780,
    height: 509,
    mobileWidth: 368,
    mobileHeight: 238,
  },
  platformName: 'SMS Marketing',
  title: 'SMS marketing for your Shopify brand',
  description:
    'Save big on your most important SMS flows. 50% cheaper than competitors with smart AI capabilities. All included with your Pietra membership.',
  backgroundColor: 'var(--pietra-spark)',
  color: '#ffffff',
  bookDemoPath: '/platform/sms-marketing/signup',
  compareToLogos: [
    { url: `${STATIC_FILE_URL}/6b58639c9d8cb0130350075ba95a7381.svg`, width: 140, height: 24 },
    { url: `${STATIC_FILE_URL}/2ea03923157b856c8c6849850ddcb9ee.svg`, width: 108, height: 32 },
  ],
  popularUseCases: {
    title: { text: 'Have Pietra set up these popular use cases for you', color: '#fff' },
    cards: [
      {
        color: '#ff5c3c',
        icon: `${STATIC_FILE_URL}/809339f7a7e05375dddfbc80539aea70.svg`,
        pill: '8X higher engagement rate',
        title: 'Welcome series',
        desc: 'This automation fires after a subscriber is created from our popup, keyword or by using our API. It welcomes them to your shop and sends them a coupon to incentivize a purchase.',
        button: {
          text: 'Book a demo',
          color: '#ff5c3c',
          bgColor: '#f9f2f1',
          onClick: (router: NextRouter) => router.push('/platform/sms-marketing/signup'),
        },
      },
      {
        color: '#ff5c3c',
        icon: `${STATIC_FILE_URL}/9f19c4ee9807994ad91e894393884358.svg`,
        pill: '+33% cart recovery',
        title: 'Abandoned cart',
        desc: 'Encourage customers to complete their purchase with this and 10% off coupon.',
        button: {
          text: 'Book a demo',
          color: '#ff5c3c',
          bgColor: '#f9f2f1',
          onClick: (router: NextRouter) => router.push('/platform/sms-marketing/signup'),
        },
      },
      {
        color: '#ff5c3c',
        icon: `${STATIC_FILE_URL}/af3672615c89b507f40a5821000d99bf.svg`,
        pill: '+18% customers return',
        title: 'Customer win back',
        desc: 'This automation fires if the subscriber has not placed an order within the last 90 days with a coupon to incentivize them.',
        button: {
          text: 'Book a demo',
          color: '#ff5c3c',
          bgColor: '#f9f2f1',
          onClick: (router: NextRouter) => router.push('/platform/sms-marketing/signup'),
        },
      },
      {
        color: '#ff5c3c',
        icon: `${STATIC_FILE_URL}/c5d7e6d20e3f56ec573f129cbab7a383.svg`,
        pill: '+26% repeat purchase',
        title: 'Loyalty offer',
        desc: "Send customers an order follow up discount to say thanks. Includes a follow up reminder a day later if they haven't used it.",
        button: {
          text: 'Book a demo',
          color: '#ff5c3c',
          bgColor: '#f9f2f1',
          onClick: (router: NextRouter) => router.push('/platform/sms-marketing/signup'),
        },
      },
      {
        color: '#ff5c3c',
        icon: `${STATIC_FILE_URL}/1369199641edd568e01041ae88729eb0.svg`,
        pill: '+18% customers return',
        title: 'Re-engage customers',
        desc: 'Re-engage lapsed customers by sending a series of automated messages at the right time.',
        button: {
          text: 'Book a demo',
          color: '#ff5c3c',
          bgColor: '#f9f2f1',
          onClick: (router: NextRouter) => router.push('/platform/sms-marketing/signup'),
        },
      },
      {
        color: '#ff5c3c',
        icon: `${STATIC_FILE_URL}/b19c74d8499bac26ca0d279908748dd3.svg`,
        pill: '+21% repeat purchases',
        title: 'Post purchase',
        desc: 'This automation flow fires 30 minutes after someone purchases and thanks them for their order and offers 10% off their next order if placed within 24 hours.',
        button: {
          text: 'Book a demo',
          color: '#ff5c3c',
          bgColor: '#f9f2f1',
          onClick: (router: NextRouter) => router.push('/platform/sms-marketing/signup'),
        },
      },
    ],
  },
};

export const ORDER_SAVINGS_COMPARISON_MATRIX: { [key: string]: OrderSavingsComparisonMap } = {
  '0,500': {
    competitor: {
      orderPickFee: 3,
      additionalItemPickFee: 0.75,
      promotionalInserts: 0.2,
      returnProcessing: "$2.00 + $0.50 per add'l unit",
    },
    pietra: {
      orderPickFee: 1.5,
      additionalItemPickFee: 0,
      promotionalInserts: 0,
      returnProcessing: '$2.00',
    },
  },
  '501,1000': {
    competitor: {
      orderPickFee: 2.5,
      additionalItemPickFee: 0.5,
      promotionalInserts: 0.2,
      returnProcessing: "$2.00 + $0.50 per add'l unit",
    },
    pietra: {
      orderPickFee: 1.5,
      additionalItemPickFee: 0,
      promotionalInserts: 0,
      returnProcessing: '$2.00',
    },
  },
  '1001,2500': {
    competitor: {
      orderPickFee: 2.5,
      additionalItemPickFee: 0.5,
      promotionalInserts: 0.2,
      returnProcessing: "$2.00 + $0.50 per add'l unit",
    },
    pietra: {
      orderPickFee: 1.5,
      additionalItemPickFee: 0,
      promotionalInserts: 0,
      returnProcessing: '$2.00',
    },
  },
  '2501,5000': {
    competitor: {
      orderPickFee: 2,
      additionalItemPickFee: 0.4,
      promotionalInserts: 0.2,
      returnProcessing: "$2.00 + $0.50 per add'l unit",
    },
    pietra: {
      orderPickFee: 1.5,
      additionalItemPickFee: 0,
      promotionalInserts: 0,
      returnProcessing: '$2.00',
    },
  },
  '5001,10000': {
    competitor: {
      orderPickFee: 1.8,
      additionalItemPickFee: 0.35,
      promotionalInserts: 0.2,
      returnProcessing: "$2.00 + $0.50 per add'l unit",
    },
    pietra: {
      orderPickFee: 1.5,
      additionalItemPickFee: 0,
      promotionalInserts: 0,
      returnProcessing: '$2.00',
    },
  },
};

export const RETAIL_CONNECT_HEADER_CONTENT: IPlatformHeaderContent = {
  platformName: 'Retail Connect',
  description:
    'Turn your product catalog into a list of relevant buyers with the only outreach platform designed for e-commerce brands to get into retail.',
  titleClass: 'retail-connect-title',
  descriptionClass: 'retail-connect-description',
  heroBanner: [
    {
      title: 'Contact retailers in every major city',
      description: 'Find retailers who are looking for new brands, from around the world, in one place.',
      imageUrl: `${STATIC_FILE_URL}/b56175463bd5604d47218753f7e6d99e.png`,
    },
    {
      title: 'Personalized pitches for every buyer',
      description: 'AI researches every buyer and retailer to create personalized proposals to close your sale.',
      imageUrl: `${STATIC_FILE_URL}/d2e54391ace4f577b9c8725f853badda.png`,
    },
    {
      title: 'Find buyers on every channel',
      description:
        'Contact buyers where they are. Send emails, Instagram DMs, or give them a call. All from one place.',
      imageUrl: `${STATIC_FILE_URL}/c72d55c6380dfd701d141db591cf5b01.png`,
    },
  ],
};

export const RETAIL_CONNECT_CONTENT: IPlatformPage = {
  type: PlatformPageType.RETAIL_CONNECT,
  title: 'Boost retail & wholesale sales with Pietra',
  sections: [
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      title: 'Get thousands of opportunities in seconds with one click.',
      description:
        'Imagine plugging in your website and getting a list of relevant retailers you can work with in any major city.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#fff',
      buttons: [{ btnText: 'View retailers for my brand' }],
      image: `${STATIC_FILE_URL}/bc5a174dce1d378c83b4c655e7c3cb29.png`,
      imageStyle: { scale: '1.2' },
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      title: 'The fee-free way to build a wholesale network.',
      description:
        'Use same tools as expensive agencies, brokers, and consultants without the fees. Cut out middleman and save money doing it.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      background:
        'linear-gradient(0deg, rgba(255, 255, 255, 0.90) 0%, rgba(255, 255, 255, 0.90) 100%), radial-gradient(127.58% 127.5% at 25.04% 20.84%, #535AFF 0%, #7D33F7 100%)',
      buttons: [{ btnText: 'Find your matches' }],
      image: `${STATIC_FILE_URL}/3c07d587fcd7a7a2b73a8d2d61645c41.png`,
      imageStyle: { scale: '1.2' },
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      title: 'Let AI research retailers and buyers to craft the perfect pitch.',
      description:
        'Every brand is unique. Pietra AI matches your products to the right retailers, then suggests the best message to close the sale.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#fff',
      buttons: [{ btnText: 'Get started for free' }],
      image: `${STATIC_FILE_URL}/3e8b6af976a23ceee8f445b60c8b57b0.png`,
      imageStyle: { scale: '1.2' },
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      title: "It's easier, cheaper, and faster with Pietra",
      description:
        "Easily create private pages for your wholesale products and automatically ship from Pietra's fulfillment center directly to retailers with no additional fees or revenue sharing.",
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      background:
        'linear-gradient(0deg, rgba(255, 255, 255, 0.90) 0%, rgba(255, 255, 255, 0.90) 100%), radial-gradient(127.58% 127.5% at 25.04% 20.84%, #535AFF 0%, #7D33F7 100%)',
      buttons: [{ btnText: 'Start for free' }],
      image: `${STATIC_FILE_URL}/6ee4d66254c90f58ebd01b7b9462ce90.png`,
      imageStyle: { scale: '1.2' },
    },
  ],
};

export const RETAIL_CONNECT_REVIEWS_CONTENT = [
  {
    backgroundImage: `${STATIC_FILE_URL}/d049dca5b023572d01228627a90975a4.png`,
    name: 'Magaret  T.',
    company: 'Atmo Home',
    comment: 'Retail Connect is absolutely brilliant. This is the perfect tool for my business.',
  },
  {
    backgroundImage: `${STATIC_FILE_URL}/152f86f79e36671832ea5f9793fe4fcb.png`,
    name: 'Maddie',
    company: 'Leune',
    comment: "Pietra's Retail Connect is one of the biggest value adds to our membership. ",
  },
  {
    backgroundImage: `${STATIC_FILE_URL}/e9f9a03e96b83d84453feef5e4c00606.png`,
    name: 'Rachel',
    company: 'Pickety Witch',
    comment: "This is awesome! It's a super user friendly tool.",
  },
  {
    backgroundImage: `${STATIC_FILE_URL}/7bd20d32a799889e2500b491510445e1.png`,
    name: 'Greg',
    company: 'Cowboy Ski Club',
    comment: "This is amazing! These are all retailers I'd love to work with.",
  },
  {
    backgroundImage: `${STATIC_FILE_URL}/a2fb784a7c1755e81e3cc59d8e78abc6.png`,
    name: 'Aaron',
    company: 'Aaron Evy Designs',
    comment: 'This is the single best tool that business owners can use for getting into retailers.',
  },
  {
    backgroundImage: `${STATIC_FILE_URL}/e45b5da80933b2acf9f5dc0e5503df99.png`,
    name: 'Hailey',
    company: 'Iloveyoubaby',
    comment: "I can't believe that this is included with my membership!",
  },
];

export const PRICING_REVIEWS_CONTENT = [
  {
    backgroundImage: '/images/pricing/1.png',
    title: 'Increase your gross margin.',
    desc: ['Suppliers to cut product costs.', 'Fulfillment for up to 40% less.'],
  },
  {
    backgroundImage: '/images/pricing/2.png',
    title: 'Lower your operating expenses.',
    desc: ['Cut expensive hiring & agencies.', 'Replace multiple apps with one.'],
  },
  {
    backgroundImage: '/images/pricing/3.png',
    title: 'Ensure first order profitability.',
    desc: ['Marketing apps to reduce CAC.', 'Analytics measure what matters.'],
  },
  {
    backgroundImage: '/images/pricing/4.png',
    title: 'Increase customer LTV.',
    desc: ['Source products to increase AOV.', 'Diversify into wholesale & retail.'],
  },
  {
    backgroundImage: '/images/pricing/5.png',
    title: 'Drive organic demand.',
    desc: ['Spot trends before they happen.', 'Engage & manage influencers.'],
  },
  {
    backgroundImage: '/images/pricing/6.png',
    title: 'Launch new products quickly.',
    desc: ['Prototype new designs with AI.', 'Work directly with top factories.'],
  },
];

export const SOCIAL_ANALYTICS_HEADER_CONTENT: IPlatformHeaderContent = {
  platformName: 'Social Analysis',
  description: 'Real-time insights and recommendations that supercharge customer acquisition and retention.',
  titleClass: 'social-analysis-title',
  descriptionClass: 'social-analysis-description',
  heroBanner: [
    {
      title: 'Problems = Profits',
      description: 'Turn CX from reactive to proactive, and get ahead of customer hiccups.',
      imageUrl: `${STATIC_FILE_URL}/675c30a08e6349065f832f13426c7f66.png`,
    },
    {
      title: 'Auto-pilot UGC',
      description: 'Stop endlessly scrolling social, and get content & insights in real-time.',
      imageUrl: `${STATIC_FILE_URL}/04edadbf2454679729e226a57d96bcb2.png`,
    },
    {
      title: 'One-up Competitors',
      description: 'Stop stalking your competition, and identify trends before they can.',
      imageUrl: `${STATIC_FILE_URL}/8ae1e8c8679a2883dd5466d667c6ab77.png`,
    },
  ],
};

export const SOCIAL_ANALYTICS_CONTENT: IPlatformPage = {
  type: PlatformPageType.SOCIAL_ANALYSIS,
  title: 'Level up your marketing with real-time social insights',
  sections: [
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      title: 'Time = Money',
      description:
        'Pulse analyzes and curates billions of social data-points, saving you hundreds of hours, and surfaces revenue-driving insights and recommendations in real-time.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      background:
        'linear-gradient(0deg, rgba(255, 255, 255, 0.90) 0%, rgba(255, 255, 255, 0.90) 100%), radial-gradient(127.58% 127.5% at 25.04% 20.84%, #535AFF 0%, #7D33F7 100%)',
      buttons: [{ btnText: 'Get your free brand report' }],
      image: `${STATIC_FILE_URL}/51c77597b0fc99b1837037c2af12b9c4.png`,
      imageStyle: { scale: '1.2' },
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      title: 'On-brand Influencers',
      description:
        "Set your preferences, and Pulse automatically tailors influencer shortlists to match your brand's vibe.",
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#fff',
      buttons: [{ btnText: 'Get your free influencer report' }],
      image: `${STATIC_FILE_URL}/e3a67848d954c5be35eaf660facd9d23.png`,
      imageStyle: { scale: '1.2' },
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      title: 'Welcome competition',
      description:
        'Pulse monitors your competition, providing data and recommendations that helps you stay ahead and win.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      background:
        'linear-gradient(0deg, rgba(255, 255, 255, 0.90) 0%, rgba(255, 255, 255, 0.90) 100%), radial-gradient(127.58% 127.5% at 25.04% 20.84%, #535AFF 0%, #7D33F7 100%)',
      buttons: [{ btnText: 'Get your free competitive analysis' }],
      image: `${STATIC_FILE_URL}/d851e16f03cf2eeb03071f027b615279.png`,
      imageStyle: { scale: '1.2' },
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      title: 'Skyrocket revenue',
      description:
        'Pulse scours the entire web, and surfaces acquisition channels and topics that match your brand and products.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#fff',
      buttons: [{ btnText: 'Get your free growth checklist' }],
      image: `${STATIC_FILE_URL}/abde28409f5963337e564b802d46f215.png`,
      imageStyle: { scale: '1.2' },
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      title: 'No trench, zone',
      description:
        'Pulse does the heavy-lifting, curates from billions of data-sources, and surfaces insights and recommendations in real-time, so you can focus on what you love — your business.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      background:
        'linear-gradient(0deg, rgba(255, 255, 255, 0.90) 0%, rgba(255, 255, 255, 0.90) 100%), radial-gradient(127.58% 127.5% at 25.04% 20.84%, #535AFF 0%, #7D33F7 100%)',
      buttons: [{ btnText: 'Get your free growth insights & recommendations' }],
      image: `${STATIC_FILE_URL}/228dd0ce4ee9e5dd0d5eebbf49aa04c2.png`,
      imageStyle: { scale: '1.2' },
    },
  ],
};

export const SOCIAL_ANALYTICS_MARKETING_TOOL_CONTENT = [
  {
    icon: `${STATIC_FILE_URL}/c11bdafdd854c35901e5ab37fc5bd5ad.svg`,
    title: 'Retail Connect',
    description: 'Reach out to buyers and sell your products in local shops with no fees.',
    linkText: 'Learn more',
    link: '/platform/retail-connect',
  },
  {
    icon: `${STATIC_FILE_URL}/25a50d1cd93bfb1a6870f19ee0e0c4b4.svg`,
    title: 'Analytics',
    description: 'Monitor your customers, orders, products, and marketing channels.',
    linkText: 'Learn more',
    link: '/platform/analytics',
  },
];

export const SALES_AND_MARKETING_HEADER_CONTENT: IPlatformHeaderContent = {
  platformName: 'Sales And Marketing',
  description:
    'Pietra gives you access to software & services to grow online and offline sales. Save more than $10,000/mo vs. purchasing full price software.',
  titleClass: 'sales-and-marketing-title',
  descriptionClass: 'sales-and-marketing-description',
  heroTitle: 'Get all of these for one low monthly price',
  heroBanner: [
    {
      title: 'Retail Connect',
      description: 'Contact buyers to get into retailers around the globe',
      remark: 'Compare to: an agency',
      imageUrl: `${STATIC_FILE_URL}/ac877db962ba367f3111b76bbf01d91c.svg`,
    },
    {
      title: 'Pietra Pulse',
      description: 'Find influencers, competitors and find new customer segments.',
      remark: 'Compare to: archiveGPT',
      imageUrl: `${STATIC_FILE_URL}/481955059ee57e54598f4797ec1144d9.svg`,
    },
    {
      title: 'PR Boxes & Gifting',
      description: 'Create stunning PR boxes, ship to influencers, and monitor results.',
      remark: 'Compare to: PR agency',
      imageUrl: `${STATIC_FILE_URL}/047e6f1143335c02b3c37b9f552a44ca.svg`,
    },
    {
      title: 'Affiliate Management',
      description: 'Manage your influencer commissions and payouts',
      remark: 'Compare to: Refersion',
      imageUrl: `${STATIC_FILE_URL}/dfbbd6379a6022d2922bf7cad2904a7d.svg`,
    },
    {
      title: 'AI Content Creation',
      description: 'Create marketing and advertising assets easily.',
      remark: 'Compare to: Shutterstock',
      imageUrl: `${STATIC_FILE_URL}/8373b5eb40b1b882b29ae21964bec6ca.svg`,
    },
    {
      title: 'Monthly Marketing Strategy',
      description: 'Work with a pro to get advice on how to grow your business.',
      remark: 'Compare to: Marketing agency',
      imageUrl: `${STATIC_FILE_URL}/6e618784f671f64f3012871f9bb13cc2.svg`,
    },
  ],
};

export const SALES_AND_MARKETING_CONTENT: IPlatformPage = {
  type: PlatformPageType.SALES_AND_MARKETING,
  title: 'Sales And Marketing',
  sections: [
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      title: 'Get thousands of retail opportunities in seconds with one click.',
      titleColor: '#000000',
      description:
        'Imagine plugging in your website and getting a list of relevant retailers you can work with in any major city.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      background: `url('${STATIC_FILE_URL}/a004ce7e7118f78a83ea91b629c96249.png') lightgray 50% / cover no-repeat`,
      buttons: [{ btnText: 'View retailers for my brand' }],
      image: `${STATIC_FILE_URL}/bc5a174dce1d378c83b4c655e7c3cb29.png`,
      imageStyle: { scale: '1.2' },
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      title: 'Find influencers, monitor competitors, and find new customers.',
      titleColor: '#000000',
      description:
        'Use AI to monitor social content in real time, track competitor advertisements on Meta, and find new customer segments based on what people are talking about online.',
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      bgColor: '#fff',
      buttons: [{ btnText: 'Learn more' }],
      image: `${STATIC_FILE_URL}/c21dee4ea2a3b36febb61fb283d05d00.png`,
      imageStyle: { scale: '1.2' },
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      title: "Use Pietra's end-to-end PR box service.",
      titleColor: '#000000',
      description:
        "Create, assemble, and ship PR boxes to influencers. Pietra's team of professionals will help you bring your ideas to life and execute behind the scenes.",
      descriptionColor: 'rgba(0, 0, 0, 0.6)',
      background: `url('${STATIC_FILE_URL}/a004ce7e7118f78a83ea91b629c96249.png') lightgray 50% / cover no-repeat`,
      buttons: [{ btnText: 'Learn More' }],
      image: `${STATIC_FILE_URL}/4b15fa809a67076b451b7a1e1f539022.png`,
      imageStyle: { scale: '1.2' },
    },
  ],
};

export const SALES_AND_MARKETING_FAQ_CONTENT: FAQType[] = [
  {
    question: 'What is Pietra?',
    answer:
      'Pietra is a suite of software and services that all eCommerce operators need to run their businesses profitably. Pietra offers the full stack, everything from AI design tools, a manufacturing network of over 1300 product and packaging suppliers, a fulfillment center, and a suite of marketing tools to help you grow your sales. At Pietra we understand what it takes to bring a product to market and to get that product in the hands of your customers - we have a solution for you each step of the way! ',
  },
  {
    question: 'What does Pietra cost?',
    answer:
      "Pietra offers 3 membership plans. The Pietra Essentials plan starts at $29/mo, Pietra Business starts at $239/mo and we offer custom plans for scaled mid-market businesses. Each plan gives you access to a wide range of features, some of which will have additional costs based on usage. A full list of what's included in each plan can be found on our pricing page.",
  },
  {
    question: 'Who is Pietra for?',
    answer:
      "Pietra is designed to cater to a wide range of users, making it the perfect solution for various business needs. Founders and solopreneurs benefit from Pietra's comprehensive software and services, enabling them to manage everything on their own or with a small team. For eCommerce brands, Pietra is an essential tool for Operations teams, streamlining procurement to source new products, reducing COGs, and optimizing fulfillment to lower costs. Lastly, marketers thrive with Pietra, leveraging its powerful marketing suite to grow their businesses effectively. Pietra has the tools to help you succeed.",
  },
  {
    question: 'How do I get started?',
    answer: `<a href="/sell-on-pietra/register">Create an account here.</a> It's completely free to create an account and you'll be able to explore the platform, browse suppliers, check out fulfillment rates, and so much more. It's the best way to determine if Pietra is the right fit for your brand.`,
  },
  {
    question: 'Why do I need a membership?',
    answer:
      'Pietra was founded on the notion that by offering founders an eCommerce membership we could lower their operating costs and increase their profitability. For this reason Pietra is a business membership. Memberships start at $49/mo and scale up for brands of all sizes. Our goal is to always lower your cost when you pay for the Pietra bundle.',
  },
];

export const PIETRA_AI_HEADER_CONTENT: IPlatformHeaderContent = {
  platformName: 'Pietra AI',
  description:
    'Pietra AI learns your brand, automates your workflows, and scales your business with AI agents that work 24/7.',
  titleClass: 'pietra-ai-title',
  descriptionClass: 'pietra-ai-description',
  heroBanner: [
    {
      title: 'AI that knows you & your business',
      description:
        'Want answers fast? Just ask your AI. It can read, analyze, and access Pietra services to help you solve problems.',
      imageUrl: `${STATIC_FILE_URL}/3637c01481031922de8b80db89773ee3.png`,
    },
    {
      title: 'You now have a digital workforce',
      description: 'Use your AI to delegate repetitive tasks and let the AI do work for you.',
      imageUrl: `${STATIC_FILE_URL}/bd58192614fa88d441d32e5df697f449.png`,
    },
    {
      title: 'AI Agents built for e-commerce',
      description: 'Deploy smart AI agents that are designed for e-commerce to run your brand with ease.',
      imageUrl: `${STATIC_FILE_URL}/745f49d3f977826376262879ae551752.png`,
    },
  ],
};

export const PIETRA_AI_CONTENT: IPlatformPage = {
  type: PlatformPageType.PIETRA_AI,
  title: 'Transform your business with the first AI for e-commerce',
  sections: [
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      title: 'Ask your AI when you need to solve a problem.',
      description:
        'Your AI is trained like an employee. When you run into a business problem, just ask your AI for help. Ask your AI to find new suppliers, analyze your sales data, create marketing content, and receive strategic business advice.',
      descriptionColor: 'var(--colors-gray-2, #6B6B6B)',
      bgColor: '#fff',
      buttons: [{ btnText: 'Try Pietra AI' }],
      image: `${STATIC_FILE_URL}/4c1704da8a898d90f799f44bbb09419c.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      title: 'A digital workforce that gets work done while you sleep.',
      description:
        'Ask your AI to complete a task and sit back and wait for it to complete. Get automated email reports on sales performance, ad spend analysis, inventory alerts, and custom business intelligence delivered straight to your inbox.',
      descriptionColor: 'var(--colors-gray-2, #6B6B6B)',
      bgColor: '#FDF1F0',
      buttons: [{ btnText: 'Try Pietra AI' }],
      image: `${STATIC_FILE_URL}/ce34e438a662afec5dbf7da75cc71803.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      title: 'Deploy AI agents that work 24/7/365.',
      description:
        'Launch specialized agents that handle complete business processes end-to-end. From sourcing new products and negotiating with factories to running influencer campaigns - like having expert team members working around the clock.',
      descriptionColor: 'var(--colors-gray-2, #6B6B6B)',
      bgColor: '#fff',
      buttons: [{ btnText: 'Deploy your Agents' }],
      image: `${STATIC_FILE_URL}/38bb45cc2355296a90539f5c988e963c.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      title: 'Your business AI learns about your brand & gets smarter.',
      description:
        'Upload documents, connect Shopify, Google Analytics, ad accounts, and more to your Brand Vault. Pietra AI analyzes your brand voice, product catalog, and business metrics to provide contextual intelligence for every decision.',
      descriptionColor: 'var(--colors-gray-2, #6B6B6B)',
      bgColor: '#FDF1F0',
      buttons: [{ btnText: "Set up your brand's personalized AI" }],
      image: `${STATIC_FILE_URL}/23b27372fd5e50cc30a3f65eccf9b1e7.png`,
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      title: "Built on Pietra's world-class e-commerce infrastructure.",
      description:
        "Pietra AI enhances our proven sourcing network of 50,000+ global factories, fulfillment operations, and content creation tools. Get the power of enterprise AI built on infrastructure that's already scaled thousands of e-commerce businesses.",
      descriptionColor: 'var(--colors-gray-2, #6B6B6B)',
      bgColor: '#fff',
      buttons: [{ btnText: 'Start for free' }],
      image: `${STATIC_FILE_URL}/efa9c5038b5a7434ef3111d9082556e3.png`,
    },
  ],
};

export const CONTENT_STUDIO_HEADER_CONTENT: IPlatformHeaderContent = {
  platformName: 'Content Studio',
  description:
    'Turn your ideas into professional assets for product pages, ads, and more - quickly and without costly photo shoots.',
  titleClass: 'content-studio-title',
  descriptionClass: 'content-studio-description',
  heroBanner: [
    {
      title: 'Get started in seconds',
      description:
        "Designed for e-commerce: just provide your URL, and we'll import your product catalog - no setup required.",
      imageUrl: `${STATIC_FILE_URL}/83ca63d6d35608c9b8f67a1345efb61f.png`,
    },
    {
      title: 'Custom AI, built for your brand',
      description:
        'Ensure every asset reflects your unique style and vision using a specialized AI model, tailored for your brand.',
      imageUrl: `${STATIC_FILE_URL}/3c9763bf150857935a038fa8e3708e07.png`,
    },
    {
      title: 'On-demand marketing assets',
      description:
        'Get hundreds of professional - quality photos in minutes. Refresh your assets anytime with endless variations.',
      imageUrl: `${STATIC_FILE_URL}/97599ce26fc14c20e5757fb2257eb6a1.png`,
    },
  ],
};

export const CONTENT_STUDIO_CONTENT: IPlatformPage = {
  type: PlatformPageType.CONTENT_STUDIO,
  title: 'Create stunning marketing assets for e-commerce',
  sections: [
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      title: 'Your products deserve better than generic AI.',
      description:
        'Unlike off\u2012the\u2012shelf AI, Pietra builds a model trained exclusively on your brand. Every asset is crafted to align with your identity, guidelines, and aesthetic.',
      descriptionColor: 'var(--colors-gray-2, #6B6B6B)',
      bgColor: '#fff',
      buttons: [{ btnText: 'Create marketing content' }],
      image: `${STATIC_FILE_URL}/f4c4f0c815141d2d493f8ab4fa9e0e48.png`,
      imageStyle: { scale: '1.2' },
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      title: 'Turn ideas into assets in minutes.',
      description:
        'Generate hundreds of images for ads, emails, social media, and more - transforming one marketer into a content powerhouse with just a few clicks.',
      descriptionColor: 'var(--colors-gray-2, #6B6B6B)',
      bgColor: '#FDF1F0',
      buttons: [{ btnText: 'Create marketing content' }],
      image: `${STATIC_FILE_URL}/a5252a44a963d5bdc28610981f943b83.png`,
      imageStyle: { scale: '1.2' },
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      title: 'Say goodbye to expensive photoshoots.',
      description:
        'Create professional images in minutes and quickly refine your ideas with endless variations - no costly photo shoots or delays. Focus on scaling your brand with speed and creativity.',
      descriptionColor: 'var(--colors-gray-2, #6B6B6B)',
      bgColor: '#fff',
      buttons: [{ btnText: 'Create marketing content' }],
      image: `${STATIC_FILE_URL}/6255d7c4eae2fbbcfad0daa13984ee60.png`,
      imageStyle: { scale: '1.2' },
    },
    {
      type: PlatformPageSectionType.TEXT_IMAGE,
      title: "Campaigns are stressful. Assets don't have to be.",
      description:
        'Transform your campaign ideas into cohesive, on-brand assets that pull from your entire product line. With your custom AI model, creating tailored content for every channel has never been easier.',
      descriptionColor: 'var(--colors-gray-2, #6B6B6B)',
      bgColor: '#FDF1F0',
      buttons: [{ btnText: 'Create marketing content' }],
      image: `${STATIC_FILE_URL}/c7075af7e632bb153c0034b0166a445a.png`,
      imageStyle: { scale: '1.2' },
    },
  ],
};

export const CONTENT_STUDIO_MARKETING_TOOL_CONTENT = [
  {
    icon: `${STATIC_FILE_URL}/25a50d1cd93bfb1a6870f19ee0e0c4b4.svg`,
    title: 'Retail Connect',
    description: 'Reach out to buyers and sell your products in local shops with no fees.',
    linkText: 'Learn more',
    link: '/platform/retail-connect',
  },
  {
    icon: `${STATIC_FILE_URL}/9eefce6055e4345d93e87660a06317c5.svg`,
    title: 'Analytics',
    description: 'Monitor your customers, orders, products, and marketing channels.',
    linkText: 'Learn more',
    link: '/platform/analytics',
  },
];
